import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell, Save, Loader2, AlertCircle, CheckCircle, Plug, Hash, MessageSquare, Users, FlaskConical,
  Activity, ExternalLink, Slack as SlackIcon, Unplug, Inbox,
} from 'lucide-react';
import { ticketAlertsAPI, adminLookupAPI } from '../../../services/api';
import SettingsSidebar from '../../../components/admin/SettingsSidebar';
import WizardSteps from '../../../components/admin/WizardSteps';
import Card from '../../../components/admin/Card';
import Toggle from '../../../components/admin/Toggle';
import CopyField from '../../../components/admin/CopyField';
import Why from '../../../components/admin/Why';
import BoardRouteEditor from '../../../components/admin/integrations/BoardRouteEditor';
import TechnicianMappingTable from '../../../components/admin/integrations/TechnicianMappingTable';
import TicketAlertsStatusPanel from '../../../components/admin/integrations/TicketAlertsStatusPanel';
import TicketAlertsCatchUp from '../../../components/admin/integrations/TicketAlertsCatchUp';

// Order matters: a board needs a channel, and channels come from a connected
// workspace, so the chat platform is connected before boards are configured.
const SECTIONS = [
  { id: 'connect', label: '1. Connect ConnectWise', icon: Plug },
  { id: 'chat', label: '2. Chat platform', icon: MessageSquare },
  { id: 'boards', label: '3. Boards & events', icon: Hash },
  { id: 'technicians', label: '4. Technicians', icon: Users },
  { id: 'test', label: '5. Test alert', icon: FlaskConical },
  { id: 'status', label: 'Status', icon: Activity },
  { id: 'catchup', label: 'Catch up', icon: Inbox },
];

const PLATFORM_LABELS = { slack: 'Slack', gchat: 'Google Chat', teams: 'Microsoft Teams' };

/**
 * The same green "On" the collapsed board sections carry.
 *
 * Those get one because a collapsed section hides its own switch, so the pill
 * is the only thing saying the feature is live. The panels down here show
 * their switch, which felt like enough when it was written and was not: the
 * page teaches you that green-On means on, and a switch without one reads as
 * a switch that did not take.
 */
function OnPill({ on }) {
  if (!on) return null;
  return (
    <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-300">
      On
    </span>
  );
}

function GoogleChatCard({ gchat, onConnected, onDisconnected }) {
  const queryClient = useQueryClient();
  const [json, setJson] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [pinNumber, setPinNumber] = useState('');
  const installation = gchat.installation;

  const connectMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.connectGchat(json, projectNumber),
    onSuccess: () => {
      setJson('');
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] });
      onConnected?.();
    },
  });
  const confirmProjectMutation = useMutation({
    mutationFn: (projectNumber) => ticketAlertsAPI.setGchatProjectNumber(projectNumber),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] }),
  });
  const disconnectMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.disconnectGchat(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] });
      queryClient.removeQueries({ queryKey: ['ticket-alerts-members', 'gchat'] });
      queryClient.removeQueries({ queryKey: ['ticket-alerts-gchat-spaces'] });
      onDisconnected?.();
    },
  });

  const readFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setJson(String(reader.result || ''));
    reader.readAsText(file);
  };

  return (
    <div className="rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
      <div className="mb-2 flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
        <MessageSquare className="h-5 w-5" /> Google Chat
      </div>
      {installation ? (
        <div className="text-sm">
          <p className="flex items-center gap-2 text-green-700 dark:text-green-300">
            <CheckCircle className="h-4 w-4" /> Connected to project <strong>{installation.workspaceName}</strong>
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            Service account {installation.clientEmail} · connected {new Date(installation.installedAt).toLocaleString()}
          </p>
          {installation.lastError && <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Last error: {installation.lastError}</p>}

          {/* Card actions are delivered by the add-ons service agent of one
              Cloud project. It has to be pinned, or an add-on in any project
              could drive ticket actions by pointing at our endpoint. */}
          {installation.projectNumber ? (
            <p className="mt-2 text-xs text-neutral-500">Accepting events from Cloud project number {installation.projectNumber}.</p>
          ) : (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/25 dark:text-amber-100">
              <p className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  {installation.observedProjectNumber ? (
                    <>
                      Button presses are arriving from Google Cloud project number <strong>{installation.observedProjectNumber}</strong> and are
                      being rejected until you confirm that is your project.
                    </>
                  ) : (
                    <>Card buttons stay disabled until you confirm which Google Cloud project may send events, so an add-on in someone else's project cannot drive your tickets.</>
                  )}{' '}
                  It is the <strong>project number</strong> on your Cloud Console dashboard, next to the project ID.
                </span>
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  className="input w-56 py-1 text-xs"
                  placeholder="e.g. 234197961353"
                  value={pinNumber || installation.observedProjectNumber || ''}
                  onChange={(e) => setPinNumber(e.target.value.replace(/\D/g, ''))}
                />
                <button
                  type="button"
                  onClick={() => confirmProjectMutation.mutate(pinNumber || installation.observedProjectNumber)}
                  disabled={confirmProjectMutation.isPending || !(pinNumber || installation.observedProjectNumber)}
                  className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {confirmProjectMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                  Allow this project
                </button>
              </div>
              {confirmProjectMutation.isError && (
                <p className="mt-1 text-red-700 dark:text-red-300">{confirmProjectMutation.error?.response?.data?.error || 'Could not save it.'}</p>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/30"
            >
              {disconnectMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5" />} Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-neutral-600 dark:text-neutral-300">Upload the service-account key from your Google Cloud Chat app. It is stored encrypted and never shown again.</p>
          <input type="file" accept="application/json,.json" className="block text-xs text-neutral-600 dark:text-neutral-300" onChange={(e) => readFile(e.target.files?.[0])} />
          <textarea className="input h-28 font-mono text-xs" placeholder='…or paste the JSON here ({"type": "service_account", …})' value={json} onChange={(e) => setJson(e.target.value)} />
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Project number (optional, only if your Chat app uses "Project number" as the audience)</label>
            <input type="text" className="input" value={projectNumber} onChange={(e) => setProjectNumber(e.target.value)} placeholder="e.g. 123456789012" />
          </div>
          <button
            type="button"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending || json.trim().length < 50}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {connectMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Connect Google Chat
          </button>
          {connectMutation.isError && <p className="text-xs text-red-600 dark:text-red-400">{connectMutation.error?.response?.data?.error || 'Connection failed.'}</p>}
        </div>
      )}
      <Why title="Google Cloud setup (one time)">
        <p className="text-xs">Full step-by-step guide with screenshots-level detail: <code>docs/google-chat-setup.md</code> in the portal repository.</p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>In Google Cloud Console (signed in with a Google Workspace admin), create a project (or reuse one) and enable the <strong>Google Chat API</strong>.</li>
          <li>IAM &amp; Admin → Service Accounts → create one. <strong>No IAM role is needed</strong>: skip "Grant this service account access to project". Then Keys → Add key → JSON. Upload that file above.</li>
          <li>
            APIs &amp; Services → Google Chat API → <strong>Configuration</strong>: set the app name and description; enable <em>Receive 1:1 messages</em> and <em>Join spaces and group conversations</em>. Avatar URL (served by this portal):
            <div className="mt-1 flex items-center gap-3">
              <img src={gchat.avatarUrl} alt="Ticket Alerts avatar" className="h-10 w-10 rounded-lg" />
              <div className="flex-1"><CopyField value={gchat.avatarUrl} /></div>
            </div>
          </li>
          <li>
            Connection settings: choose <strong>HTTP endpoint URL</strong> and enter:
            <div className="mt-1"><CopyField value={gchat.eventsUrl} /></div>
            Paste the same URL into <strong>App home</strong> (Google requires it once 1:1 messages are on). Authentication audience, if shown: <strong>HTTP endpoint URL</strong> (or Project number, then enter the number above). Leave Commands and Link previews empty.
          </li>
          <li>Visibility: make the app available to your Workspace users, then in Google Chat open each space and add the app.</li>
          <li>Direct messages (Follow, on-call) work for a person once they have messaged the app once.</li>
        </ol>
      </Why>
    </div>
  );
}

/**
 * Microsoft Teams connect panel.
 *
 * Teams has no OAuth install like Slack: the MSP registers a bot in Azure and
 * pastes its credentials here. The portal cannot see any channel until the app
 * has been added to a team, because the Bot Connector host and the team id
 * only arrive on the first inbound event — so the panel says which step is
 * outstanding rather than showing an unexplained empty list.
 */
function MicrosoftTeamsCard({ teams, onConnected }) {
  const queryClient = useQueryClient();
  const [appId, setAppId] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [tenantId, setTenantId] = useState('');
  const installation = teams.installation;

  const connectMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.connectTeams({ appId: appId.trim(), appPassword, tenantId: tenantId.trim() }),
    onSuccess: () => {
      setAppPassword('');
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] });
      onConnected?.();
    },
  });
  const disconnectMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.disconnectTeams(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] });
      queryClient.removeQueries({ queryKey: ['ticket-alerts-members', 'teams'] });
      queryClient.removeQueries({ queryKey: ['ticket-alerts-teams-channels'] });
    },
  });

  return (
    <div className="rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
      <div className="mb-2 flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
        <MessageSquare className="h-5 w-5" /> Microsoft Teams
      </div>
      {installation ? (
        <div className="text-sm">
          <p className="flex items-center gap-2 text-green-700 dark:text-green-300">
            <CheckCircle className="h-4 w-4" /> Connected as <strong>{installation.workspaceName}</strong>
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            App {installation.appId} · connected {new Date(installation.installedAt).toLocaleString()}
          </p>
          {installation.lastError && <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Last error: {installation.lastError}</p>}
          {installation.teamId && installation.serviceUrlKnown ? (
            <p className="mt-2 text-xs text-neutral-500">The app is installed in a team and Microsoft has told us where to send messages.</p>
          ) : (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/25 dark:text-amber-100">
              Add the app to a team in Teams to finish. Nothing can be posted until Microsoft sends the portal its first
              event, which happens the moment the app is installed there.
            </div>
          )}
          <button
            type="button"
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isPending}
            className="mt-3 text-xs text-red-600 underline hover:no-underline dark:text-red-400"
          >
            Disconnect Microsoft Teams
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Paste the Azure bot's credentials. They are encrypted at rest and never shown again.
          </p>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Application (client) ID</label>
            <input type="text" className="input" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Client secret</label>
            <input type="password" className="input" value={appPassword} onChange={(e) => setAppPassword(e.target.value)} placeholder="the secret value, not its id" autoComplete="new-password" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">
              Directory (tenant) ID — required for a single-tenant bot, blank for multi-tenant
            </label>
            <input type="text" className="input" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
          </div>
          <button
            type="button"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending || appId.trim().length < 10 || appPassword.length < 8}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {connectMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Connect Microsoft Teams
          </button>
          {connectMutation.isError && <p className="text-xs text-red-600 dark:text-red-400">{connectMutation.error?.response?.data?.error || 'Connection failed.'}</p>}
        </div>
      )}
      <Why title="Azure and Teams setup (one time)">
        <p className="text-xs">Full step-by-step guide: <code>docs/teams-setup.md</code> in the portal repository.</p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>Azure portal → <strong>Azure Bot</strong> → Create. Pick a name, your resource group, pricing tier F0, and "Multi Tenant" or "Single Tenant" (note which you chose).</li>
          <li>On the bot → <strong>Configuration</strong>, paste this Messaging endpoint:
            <div className="mt-1"><CopyField value={teams.eventsUrl} /></div>
          </li>
          <li>Configuration → <strong>Manage</strong> next to Microsoft App ID → Certificates &amp; secrets → New client secret. Copy the <em>Value</em> immediately; Azure hides it afterwards. Paste the app id, that secret, and (single-tenant only) the directory id above.</li>
          <li>On the bot → <strong>Channels</strong> → add <strong>Microsoft Teams</strong>.</li>
          <li>Teams Developer Portal (or an app package) → create an app with the same app id, add a <em>bot</em> with scopes <em>team</em> and <em>personal</em>, then install it into the team that should receive alerts.</li>
          <li>Come back here: the channel list fills in once the app is installed, then pick a channel per board in step 3.</li>
        </ol>
      </Why>
    </div>
  );
}

/**
 * The AI panel.
 *
 * Two things are deliberate here. No vendor is named, because the portal only
 * needs a base URL and a model, and that is what keeps moving to a local box
 * a config change rather than a rewrite. And "this endpoint is ours" is a
 * separate, unticked assertion rather than a convenience toggle, because it
 * is the only thing that lets a customer's own words into a prompt.
 */
function AiCard({ ai, onSaved }) {
  const queryClient = useQueryClient();
  const config = ai?.config || {};
  const status = ai?.status || {};
  const [form, setForm] = useState({
    enabled: config.enabled === true,
    baseUrl: config.baseUrl || '',
    draftModel: config.models?.draft || '',
    summariseModel: config.models?.summarise || '',
    providerLabel: config.providerLabel || '',
    endpointIsOurs: config.endpointIsOurs === true,
    reasoningEffort: config.reasoningEffort || '',
    summariseTicket: config.features?.summariseTicket === true,
    apiKey: '',
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const saveMutation = useMutation({
    mutationFn: () =>
      ticketAlertsAPI.saveAi({
        enabled: form.enabled,
        baseUrl: form.baseUrl.trim(),
        models: { draft: form.draftModel.trim(), summarise: form.summariseModel.trim() },
        providerLabel: form.providerLabel.trim(),
        endpointIsOurs: form.endpointIsOurs,
        reasoningEffort: form.reasoningEffort,
        features: { summariseTicket: form.summariseTicket },
        // Only send the key when one was typed, so saving the form does not
        // wipe a stored key.
        ...(form.apiKey !== '' && { apiKey: form.apiKey }),
      }),
    onSuccess: () => {
      set({ apiKey: '' });
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] });
      onSaved?.();
    },
  });
  const testMutation = useMutation({ mutationFn: () => ticketAlertsAPI.testAi() });

  return (
    <div className="rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
      <div className="mb-2 flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
        <MessageSquare className="h-5 w-5" /> AI assistance
      </div>
      <p className="mb-3 text-xs text-neutral-500 dark:text-neutral-400">
        Optional, and off until you configure it. Point this at any endpoint that speaks the OpenAI
        chat-completions shape: a hosted provider, or your own Ollama or vLLM box. Nothing is sent anywhere
        until you fill this in.
      </p>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
          <input
            type="checkbox"
            className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            checked={form.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
          />
          Enable AI assistance
        </label>

        {form.enabled && (
          <>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">
                Endpoint base URL
              </label>
              <input
                type="text"
                className="input"
                value={form.baseUrl}
                onChange={(e) => set({ baseUrl: e.target.value })}
                placeholder="https://api.example.com/v1  or  http://192.168.1.50:11434/v1"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Model for short rewrites</label>
                <input type="text" className="input" value={form.draftModel} onChange={(e) => set({ draftModel: e.target.value })} placeholder="a small, fast model" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Model for summaries (blank = same)</label>
                <input type="text" className="input" value={form.summariseModel} onChange={(e) => set({ summariseModel: e.target.value })} placeholder="a better model" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">
                Reasoning effort
              </label>
              <select
                className="input"
                value={form.reasoningEffort}
                onChange={(e) => set({ reasoningEffort: e.target.value })}
              >
                <option value="">Don't send this setting (most models)</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                Only for reasoning models (gpt-oss, o-series, DeepSeek-R1 and similar). They spend
                the same output budget on thinking as on the answer, so at the default effort the
                answer can be cut off mid-sentence and look like a bad model. <strong>Low</strong> is
                the right setting for both jobs here. Leave this blank for anything else — an endpoint
                that does not know the parameter may reject the request.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">
                API key {status.hasKey ? '(one is stored; leave blank to keep it)' : '(leave blank for a local endpoint)'}
              </label>
              <input type="password" className="input" value={form.apiKey} onChange={(e) => set({ apiKey: e.target.value })} autoComplete="new-password" />
            </div>

            {/* The privacy gate. Not a preference — a statement about where
                this endpoint runs, and the only thing that lets a customer's
                own words into a prompt. A tick box invites ticking, so it is
                a choice between two named answers with the consequence of
                each written next to it. */}
            <fieldset className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
              <legend className="px-1 text-sm font-medium text-amber-900 dark:text-amber-100">
                Where does this endpoint run?
              </legend>
              <p className="mb-2 text-xs text-amber-900/80 dark:text-amber-100/80">
                This decides what may be sent. It is the only setting that does.
              </p>

              <label className="flex cursor-pointer items-start gap-2 rounded p-2 text-sm text-amber-900 hover:bg-amber-100/60 dark:text-amber-100 dark:hover:bg-amber-900/30">
                <input
                  type="radio"
                  name="ai-endpoint-ownership"
                  className="mt-1 border-amber-400 text-amber-600 focus:ring-amber-500"
                  checked={!form.endpointIsOurs}
                  onChange={() => set({ endpointIsOurs: false })}
                />
                <span>
                  A third-party service
                  <span className="mt-0.5 block text-xs">
                    Only text your own technicians wrote is sent. Summaries skip the customer's messages and say
                    so. Correct for any provider you do not run yourself, however good their retention policy.
                  </span>
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-2 rounded p-2 text-sm text-amber-900 hover:bg-amber-100/60 dark:text-amber-100 dark:hover:bg-amber-900/30">
                <input
                  type="radio"
                  name="ai-endpoint-ownership"
                  className="mt-1 border-amber-400 text-amber-600 focus:ring-amber-500"
                  checked={form.endpointIsOurs}
                  onChange={() => set({ endpointIsOurs: true })}
                />
                <span>
                  Infrastructure we control
                  <span className="mt-0.5 block text-xs">
                    Your own hardware, or your own private network. The whole ticket goes, including what your
                    customers wrote.
                  </span>
                </span>
              </label>

              {form.endpointIsOurs && (
                <p className="mt-1 rounded bg-amber-100 px-2 py-1.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                  Customer-written text will be sent to this endpoint.
                </p>
              )}
            </fieldset>

            <div className="space-y-1 pt-1">
              <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300">What it may do</p>
              <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" checked={form.summariseTicket} onChange={(e) => set({ summariseTicket: e.target.checked })} />
                Summarise a ticket, from the card or with <code>summarise 12345</code>
              </label>
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save AI settings
          </button>
          {form.enabled && (
            <button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-600"
            >
              {testMutation.isPending ? 'Testing…' : 'Test endpoint'}
            </button>
          )}
        </div>

        {saveMutation.isError && (
          <p className="text-xs text-red-600 dark:text-red-400">{saveMutation.error?.response?.data?.error || 'Could not save.'}</p>
        )}
        {testMutation.data && (
          <p className={`text-xs ${testMutation.data.data?.ok ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-400'}`}>
            {testMutation.data.data?.ok
              ? `Answered in ${testMutation.data.data.ms} ms. This test sends no ticket data.`
              : `No answer: ${testMutation.data.data?.error}`}
          </p>
        )}
        {status.lastError && !testMutation.data && (
          <p className="text-xs text-amber-600 dark:text-amber-400">Last error: {status.lastError}</p>
        )}
      </div>
    </div>
  );
}

export default function TicketAlertsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState({ enabled: false, boards: [], reply: { defaultCustomerFacing: false }, timeReminder: {} });
  const [dirty, setDirty] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [testTicketId, setTestTicketId] = useState('');
  const [testChannelId, setTestChannelId] = useState('');
  const [testPlatform, setTestPlatform] = useState('');
  const [banner, setBanner] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket-alerts-config'],
    queryFn: () => ticketAlertsAPI.get(),
    retry: false,
  });
  const overview = data?.data;
  const featureDisabled = error?.response?.data?.code === 'FEATURE_DISABLED';
  const slackInstallation = overview?.slack?.installation || null;
  const gchatInstallation = overview?.gchat?.installation || null;
  const slackConnected = !!slackInstallation;
  const gchatConnected = !!gchatInstallation;
  const teamsInstallation = overview?.teams?.installation || null;
  const teamsConnected = !!teamsInstallation;
  const connectedPlatforms = useMemo(
    () => [slackConnected && 'slack', gchatConnected && 'gchat', teamsConnected && 'teams'].filter(Boolean),
    [slackConnected, gchatConnected, teamsConnected]
  );
  const anyConnected = connectedPlatforms.length > 0;

  useEffect(() => {
    if (overview?.config) {
      setConfig(overview.config);
      setDirty(false);
    }
  }, [overview]);

  // Result of the Slack OAuth redirect.
  useEffect(() => {
    const slack = searchParams.get('slack');
    if (!slack) return;
    if (slack === 'connected') {
      setBanner({ type: 'success', text: 'Slack workspace connected.' });
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] });
    } else {
      setBanner({ type: 'error', text: `Slack connection failed: ${searchParams.get('reason') || 'unknown error'}` });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('slack');
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  const boardsQuery = useQuery({
    queryKey: ['admin-lookup-boards'],
    queryFn: () => adminLookupAPI.boards(),
    enabled: !!overview,
    staleTime: 10 * 60 * 1000,
    select: (resp) => resp.data?.items || [],
  });
  const prioritiesQuery = useQuery({
    queryKey: ['admin-lookup-priorities'],
    queryFn: () => adminLookupAPI.priorities(),
    enabled: !!overview,
    staleTime: 10 * 60 * 1000,
    select: (resp) => resp.data?.items || [],
  });
  const slackChannelsQuery = useQuery({
    queryKey: ['ticket-alerts-slack-channels'],
    queryFn: () => ticketAlertsAPI.slackChannels(),
    enabled: slackConnected,
    staleTime: 60 * 1000,
    select: (resp) => resp.data || [],
  });
  const gchatSpacesQuery = useQuery({
    queryKey: ['ticket-alerts-gchat-spaces'],
    queryFn: () => ticketAlertsAPI.gchatSpaces(),
    enabled: gchatConnected,
    staleTime: 60 * 1000,
    select: (resp) => resp.data || [],
  });
  const teamsChannelsQuery = useQuery({
    queryKey: ['ticket-alerts-teams-channels'],
    queryFn: () => ticketAlertsAPI.teamsChannels(),
    enabled: teamsConnected,
    staleTime: 60 * 1000,
    // The endpoint answers with { channels, pending } so it can explain an
    // empty list instead of looking broken.
    select: (resp) => resp.data?.channels || [],
  });
  // Same keys as the Technicians table so both share one request per platform.
  const slackMembersQuery = useQuery({
    queryKey: ['ticket-alerts-members', 'slack'],
    queryFn: () => ticketAlertsAPI.members(false, 'slack'),
    enabled: slackConnected,
    select: (resp) => resp.data || [],
  });
  const gchatMembersQuery = useQuery({
    queryKey: ['ticket-alerts-members', 'gchat'],
    queryFn: () => ticketAlertsAPI.members(false, 'gchat'),
    enabled: gchatConnected,
    select: (resp) => resp.data || [],
  });
  const teamsMembersQuery = useQuery({
    queryKey: ['ticket-alerts-members', 'teams'],
    queryFn: () => ticketAlertsAPI.members(false, 'teams'),
    enabled: teamsConnected,
    select: (resp) => resp.data || [],
  });

  const platforms = useMemo(
    () => ({
      slack: { connected: slackConnected, channels: slackChannelsQuery.data || [], members: slackMembersQuery.data || [] },
      gchat: { connected: gchatConnected, channels: gchatSpacesQuery.data || [], members: gchatMembersQuery.data || [] },
      teams: { connected: teamsConnected, channels: teamsChannelsQuery.data || [], members: teamsMembersQuery.data || [] },
    }),
    [
      slackConnected,
      gchatConnected,
      teamsConnected,
      slackChannelsQuery.data,
      gchatSpacesQuery.data,
      teamsChannelsQuery.data,
      slackMembersQuery.data,
      gchatMembersQuery.data,
      teamsMembersQuery.data,
    ]
  );

  // "Send now" on the unlogged-time reminder panel. The dead-code audit
  // (03d4a10) removed this and its API wrapper while the panel still read
  // them, so the whole page threw once the reminder section rendered.
  const runReminder = useMutation({ mutationFn: () => ticketAlertsAPI.runTimeReminder() });

  const saveMutation = useMutation({
    mutationFn: (cfg) => ticketAlertsAPI.update(cfg),
    onSuccess: (resp) => {
      setSaveResult({ reconcile: resp.data?.reconcile, reconcileError: resp.data?.reconcileError, at: Date.now() });
      queryClient.setQueryData(['ticket-alerts-config'], resp);
      setDirty(false);
      setTimeout(() => setSaveResult((r) => (r && Date.now() - r.at > 5000 ? null : r)), 6000);
    },
  });
  const testConnMutation = useMutation({ mutationFn: () => ticketAlertsAPI.testConnection() });
  const reregisterMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.reregisterCallbacks(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] }),
  });
  const installMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.slackInstallUrl(),
    onSuccess: (resp) => {
      if (resp.data?.url) window.location.assign(resp.data.url);
    },
  });
  const disconnectMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.disconnectSlack(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] });
      queryClient.removeQueries({ queryKey: ['ticket-alerts-members', 'slack'] });
      queryClient.removeQueries({ queryKey: ['ticket-alerts-slack-channels'] });
    },
  });
  const digestMutation = useMutation({
    mutationFn: ({ boardId, routeId }) => ticketAlertsAPI.sendDigestNow(boardId, routeId),
    onSuccess: () => {
      setBanner({ type: 'success', text: 'Digest queued. It will appear in a few seconds.' });
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-events'] });
    },
    onError: (err) => setBanner({ type: 'error', text: err?.response?.data?.error || 'Could not queue the digest.' }),
  });
  const effectiveTestPlatform = testPlatform || connectedPlatforms[0] || 'slack';
  const testAlertMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.sendTestAlert(parseInt(testTicketId, 10), testChannelId || null, effectiveTestPlatform),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-events'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-config'] });
    },
  });

  const update = (patch) => {
    setConfig((c) => ({ ...c, ...patch }));
    setDirty(true);
  };

  const steps = useMemo(() => {
    const cwOk = !!overview?.connectwise?.publicApiUrl;
    const boardsOk = config.boards.length > 0 && config.boards.every((b) => b.channelId);
    const chatOk = anyConnected;
    const testOk = !!overview?.activity?.lastTestAt || !!overview?.activity?.lastCallbackAt;
    const states = [cwOk, chatOk, chatOk && boardsOk, chatOk && boardsOk, testOk];
    const firstTodo = states.findIndex((s) => !s);
    // Status and Catch up are tools, not setup steps.
    return SECTIONS.filter((s) => s.id !== 'status' && s.id !== 'catchup').map((s, i) => ({
      id: s.id,
      label: s.label.replace(/^\d+\.\s*/, ''),
      state: states[i] ? 'done' : i === firstTodo ? 'current' : 'todo',
    }));
  }, [overview, config.boards, anyConnected]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // ---- Gates ----------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 text-neutral-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading Ticket Alerts…
      </div>
    );
  }
  if (featureDisabled) {
    return (
      <div className="mx-auto max-w-3xl">
        <Header />
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-900/20">
          <h2 className="mb-2 flex items-center gap-2 font-semibold text-amber-800 dark:text-amber-200">
            <AlertCircle className="h-5 w-5" /> Ticket Alerts is turned off
          </h2>
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Enable the <code>module.ticket_alerts</code> feature flag to unlock this integration, then come back here.
          </p>
          <Link to="/admin/feature-flags" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400">
            Open Feature Flags <ExternalLink className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-3xl">
        <Header />
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          <AlertCircle className="h-5 w-5" /> Failed to load: {error.response?.data?.error || error.message}
        </div>
      </div>
    );
  }

  const cw = overview.connectwise;
  const slackApp = overview.slack;
  const testChannels = platforms[effectiveTestPlatform]?.channels || [];

  return (
    <div className="mx-auto max-w-6xl">
      <Header />

      {banner && (
        <div
          className={`mb-4 flex items-center justify-between gap-2 rounded-lg border p-3 text-sm ${
            banner.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300'
          }`}
        >
          <span className="flex items-center gap-2">
            {banner.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {banner.text}
          </span>
          <button type="button" onClick={() => setBanner(null)} className="text-xs underline">Dismiss</button>
        </div>
      )}

      {/* Sticky control bar */}
      <div className="sticky top-16 z-10 mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-neutral-700 dark:bg-neutral-800/95">
        <WizardSteps steps={steps} onSelect={scrollTo} />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
            <Toggle checked={config.enabled} onChange={(v) => update({ enabled: v })} label="Enable ticket alerts" />
            {config.enabled ? 'Enabled' : 'Disabled'}
          </label>
          {saveResult && !dirty && !saveMutation.isPending && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <CheckCircle className="h-3.5 w-3.5" /> Saved
            </span>
          )}
          {saveMutation.isError && (
            <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
              <AlertCircle className="h-3.5 w-3.5" /> {saveMutation.error?.response?.data?.error || 'Save failed'}
            </span>
          )}
          <button
            type="button"
            onClick={() => saveMutation.mutate(config)}
            disabled={!dirty || saveMutation.isPending}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
        </div>
      </div>

      {/* The saved switch, not the local one: a test alert posts even while
          alerts are off, which otherwise reads as "everything works". */}
      {overview.config?.enabled === false && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/25 dark:text-amber-100">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            <strong>Alerts are switched off.</strong> Nothing posts for real ticket activity and the daily digest is skipped, even though test
            alerts still work. Turn on the <strong>Enable</strong> toggle above and press Save.
          </span>
        </div>
      )}

      {saveResult?.reconcileError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>Settings saved, but ConnectWise callback registration failed: {saveResult.reconcileError}. Fix the connection and use "Re-register callbacks" in Status.</span>
        </div>
      )}
      {saveResult?.reconcile?.errors?.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          Some callbacks could not be registered:
          <ul className="ml-5 list-disc">
            {saveResult.reconcile.errors.map((e) => (
              <li key={e.boardId}>Board {e.boardId}: {e.error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-8">
        <SettingsSidebar sections={SECTIONS.map(({ id, label }) => ({ id, label }))} />

        <div className="min-w-0 flex-1 space-y-6">
          {/* 1. Connect ConnectWise */}
          <Card id="connect" icon={Plug} title="1. Connect ConnectWise" description="This portal already talks to ConnectWise with the API keys configured on the server. Confirm the connection and that ConnectWise can reach this portal.">
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase text-neutral-500">Site</dt>
                <dd className="text-sm text-neutral-900 dark:text-white">{cw.siteUrl}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-neutral-500">Company ID</dt>
                <dd className="text-sm text-neutral-900 dark:text-white">{cw.companyId}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs uppercase text-neutral-500">Callback URL registered in ConnectWise</dt>
                <dd className="mt-1">
                  <CopyField value={cw.callbackBaseUrl ? `${cw.callbackBaseUrl}/<token>` : null} />
                </dd>
              </div>
            </dl>
            {!cw.publicApiUrl && (
              <p className="mt-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="h-4 w-4" /> The server has no public API URL configured (API_URL). Callbacks cannot be registered until it is set.
              </p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => testConnMutation.mutate()}
                disabled={testConnMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
              >
                {testConnMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                Test connection
              </button>
              {testConnMutation.data && (
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    {testConnMutation.data.data.connectwise?.success ? <CheckCircle className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-red-500" />}
                    ConnectWise API: {testConnMutation.data.data.connectwise?.success ? 'OK' : testConnMutation.data.data.connectwise?.error}
                  </div>
                  <div className="flex items-center gap-2">
                    {testConnMutation.data.data.callbackUrl?.reachable ? <CheckCircle className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-red-500" />}
                    Callback URL reachable: {testConnMutation.data.data.callbackUrl?.reachable ? 'yes' : testConnMutation.data.data.callbackUrl?.error || `HTTP ${testConnMutation.data.data.callbackUrl?.status}`}
                  </div>
                </div>
              )}
              {testConnMutation.isError && <span className="text-sm text-red-600">Test failed.</span>}
            </div>
            <Why>
              <p>The portal registers one callback per board in ConnectWise (System → Callbacks) so you never configure them by hand. ConnectWise then POSTs to the URL above whenever a ticket on that board is added, updated, or deleted.</p>
              <p>The callback body is never trusted; the portal re-reads the ticket from the ConnectWise API before doing anything.</p>
              <p>If "Callback URL reachable" fails, ConnectWise cannot reach this portal either. Check the public API URL and that the API is deployed.</p>
            </Why>
          </Card>

          {/* 2. Chat platform */}
          <Card id="chat" icon={MessageSquare} title="2. Chat platform" description="Where alerts are delivered. Connect Slack, Google Chat, Microsoft Teams, or any combination; each board picks its platform in step 3.">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
                <div className="mb-2 flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                  <SlackIcon className="h-5 w-5" /> Slack
                </div>
                {!slackApp.appConfigured ? (
                  <div className="text-sm text-neutral-600 dark:text-neutral-300">
                    <p className="flex items-center gap-2 text-amber-700 dark:text-amber-300"><AlertCircle className="h-4 w-4" /> Slack app credentials are not configured on the server.</p>
                    <p className="mt-2">Create the app from <code>docs/slack-app-manifest.json</code> at api.slack.com/apps, then set <code>SLACK_CLIENT_ID</code>, <code>SLACK_CLIENT_SECRET</code>, and <code>SLACK_SIGNING_SECRET</code> on the API server. Redirect URL:</p>
                    <div className="mt-1"><CopyField value={slackApp.redirectUri} /></div>
                  </div>
                ) : slackConnected ? (
                  <div className="text-sm">
                    <p className="flex items-center gap-2 text-green-700 dark:text-green-300">
                      <CheckCircle className="h-4 w-4" /> Connected to <strong>{slackInstallation.workspaceName}</strong>
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">Installed {new Date(slackInstallation.installedAt).toLocaleString()}</p>
                    {slackInstallation.lastError && <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Last error: {slackInstallation.lastError}</p>}
                    {slackInstallation.missingScopes?.length > 0 && (
                      <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                        This install predates a permission this version needs ({slackInstallation.missingScopes.join(', ')}). Click Reconnect to grant it; Follow and on-call direct messages need it.
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(slackInstallation.missingScopes?.length > 0 || slackInstallation.lastError) && (
                        <button
                          type="button"
                          onClick={() => installMutation.mutate()}
                          disabled={installMutation.isPending}
                          className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
                        >
                          {installMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SlackIcon className="h-3.5 w-3.5" />} Reconnect
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => disconnectMutation.mutate()}
                        disabled={disconnectMutation.isPending}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/30"
                      >
                        {disconnectMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5" />} Disconnect
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm">
                    <p className="text-neutral-600 dark:text-neutral-300">Install the HaloMTS Ticket Alerts app into your workspace. You will be sent to Slack to approve it and brought back here.</p>
                    <button
                      type="button"
                      onClick={() => installMutation.mutate()}
                      disabled={installMutation.isPending}
                      className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#4A154B] px-4 py-2 text-sm font-medium text-white hover:bg-[#3b1040] disabled:opacity-50"
                    >
                      {installMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SlackIcon className="h-4 w-4" />}
                      Add to Slack
                    </button>
                    {installMutation.isError && <p className="mt-2 text-xs text-red-600">{installMutation.error?.response?.data?.error || 'Could not start the Slack install.'}</p>}
                  </div>
                )}
              </div>
              <GoogleChatCard
                gchat={overview.gchat}
                onConnected={() => setBanner({ type: 'success', text: 'Google Chat connected. Add the app to a space, then pick that space for a board in step 3.' })}
              />
              {overview.ai && <AiCard ai={overview.ai} onSaved={() => setBanner({ type: 'success', text: 'AI settings saved.' })} />}
              {overview.teams && (
                <MicrosoftTeamsCard
                  teams={overview.teams}
                  onConnected={() =>
                    setBanner({
                      type: 'success',
                      text: 'Microsoft Teams connected. Install the app into a team, then pick that channel for a board in step 3.',
                    })
                  }
                />
              )}
            </div>
            <Why>
              <p>One Slack app, owned by HaloMTS, is installed into your workspace with OAuth. For Google Chat you run your own Chat app in your Google Cloud project and the portal authenticates with its service account. Tokens and keys are encrypted at rest and never shown in the portal.</p>
              <p>Slack public channels are joined automatically when the first alert is posted; private channels need <code>/invite @Ticket Alerts</code>. Google Chat spaces need the app added by a member.</p>
              <p>If a platform reports the app was removed, the connection shows as revoked here and alerts to it pause until you reconnect.</p>
            </Why>
          </Card>

          {/* 3. Boards & events */}
          <Card id="boards" icon={Hash} title="3. Boards & events" description="Pick the service boards that should alert, what to alert on, and where each board posts.">
            {boardsQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading boards…</div>
            ) : (
              <BoardRouteEditor
                routes={config.boards}
                availableBoards={boardsQuery.data || []}
                platforms={platforms}
                priorities={prioritiesQuery.data || []}
                eventTypes={overview.eventTypes}
                notifyModes={overview.notifyModes}
                defaultTimezone={cw.defaultTimezone}
                onSendDigest={(boardId, routeId) => digestMutation.mutate({ boardId, routeId })}
                digestSendingRouteKey={digestMutation.isPending ? (digestMutation.variables?.routeId || digestMutation.variables?.boardId) : null}
                onChange={(boards) => update({ boards })}
              />
            )}
            <div className="mt-6 flex items-center justify-between gap-4 rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-white">Notes default to Discussion</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Note &amp; time opens on <strong>Discussion</strong>, visible to the contact in the portal, rather than
                  Internal. Somebody writing a note has almost always just answered a customer. Visibility is not
                  emailing: nothing is sent unless a recipient is ticked, and the type can be changed every time.
                </p>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <OnPill on={config.reply?.defaultCustomerFacing === true} />
                <Toggle checked={config.reply?.defaultCustomerFacing === true} onChange={(v) => update({ reply: { ...config.reply, defaultCustomerFacing: v } })} label="Notes default to Discussion" />
              </div>
            </div>
            <div className="mt-6 rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">Remind people about time they have not logged</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    At the end of their own working day, anyone who finished it having recorded nothing — plus the
                    specific tickets they worked without logging against — gets a direct message and an email, with a
                    button to log it there and then. Nobody with nothing outstanding is messaged.
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <OnPill on={config.timeReminder?.enabled === true} />
                  <Toggle
                    checked={config.timeReminder?.enabled === true}
                    onChange={(v) => update({ timeReminder: { ...config.timeReminder, enabled: v } })}
                    label="Remind people about unlogged time"
                  />
                </div>
              </div>

              {config.timeReminder?.enabled && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Time</label>
                    <input
                      type="time"
                      value={config.timeReminder?.time || '17:00'}
                      onChange={(e) => update({ timeReminder: { ...config.timeReminder, time: e.target.value } })}
                      className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-600 dark:bg-neutral-700 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Each person's own clock.</p>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Days</label>
                    <div className="flex gap-1">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => {
                        const days = config.timeReminder?.days || [1, 2, 3, 4, 5];
                        const on = days.includes(i);
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() =>
                              update({
                                timeReminder: {
                                  ...config.timeReminder,
                                  days: on ? days.filter((x) => x !== i) : [...days, i].sort(),
                                },
                              })
                            }
                            className={`h-8 w-8 rounded text-xs ${
                              on
                                ? 'bg-primary-600 text-white'
                                : 'border border-neutral-300 text-neutral-500 dark:border-neutral-600 dark:text-neutral-400'
                            }`}
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">
                      Hours floor (optional)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="24"
                      step="0.5"
                      value={config.timeReminder?.minHours ?? 0}
                      onChange={(e) => update({ timeReminder: { ...config.timeReminder, minHours: Number(e.target.value) } })}
                      className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-600 dark:bg-neutral-700 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      0 is off. Leave it there unless everyone works fixed hours — a floor is noise on a short day.
                    </p>
                  </div>
                  <div className="flex flex-col justify-end">
                    <button
                      type="button"
                      onClick={() => runReminder.mutate()}
                      disabled={runReminder.isPending}
                      className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-700 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-300"
                    >
                      {runReminder.isPending ? 'Sending…' : 'Send now'}
                    </button>
                    {runReminder.data && (
                      <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">{runReminder.data.data?.message}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Why>
              <p><strong>Filters</strong> are checked against the ticket as re-read from ConnectWise, so a ticket that moves to another board or changes priority is routed correctly.</p>
              <p><strong>Existing tickets</strong> that were never alerted are recorded quietly on their first update so later changes can be detected; a card appears only when something you subscribed to happens.</p>
              <p><strong>After-hours</strong> routing, the <strong>daily digest</strong>, and <strong>SLA warnings</strong> are configured per board inside each card. Direct messages (Follow, on-call) need the Slack app's im:write permission; reconnect Slack if the chat step shows a permission warning. On Google Chat, a person receives direct messages once they have messaged the app.</p>
              <p>Save after changing boards so callbacks are added or removed in ConnectWise.</p>
            </Why>
          </Card>

          {/* 4. Technicians */}
          <Card id="technicians" icon={Users} title="4. Technicians" description="Link chat users to ConnectWise members so 'Take it', assignments, and notes are attributed correctly.">
            <TechnicianMappingTable connectedPlatforms={connectedPlatforms} />
            <Why>
              <p>Users are matched automatically by email against portal users and ConnectWise members. Fix misses with "Link".</p>
              <p>A linked technician's notes show them as the author in ConnectWise. An unlinked user can still reply; the note is posted by the integration account with their name on the first line, but they cannot take or be assigned tickets.</p>
            </Why>
          </Card>

          {/* 5. Test alert */}
          <Card id="test" icon={FlaskConical} title="5. Send a test alert" description="Post a sample card for a real ticket. Test cards have their action buttons disabled, so nothing is written to ConnectWise.">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Ticket number</label>
                <input type="number" className="input w-40" value={testTicketId} onChange={(e) => setTestTicketId(e.target.value)} placeholder="e.g. 12345" />
              </div>
              {connectedPlatforms.length > 1 && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">Platform</label>
                  <select className="input w-40" value={effectiveTestPlatform} onChange={(e) => { setTestPlatform(e.target.value); setTestChannelId(''); }}>
                    {connectedPlatforms.map((p) => (
                      <option key={p} value={p}>{PLATFORM_LABELS[p]}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-600 dark:text-neutral-300">{effectiveTestPlatform === 'gchat' ? 'Space' : 'Channel'} (optional)</label>
                <select className="input w-56" value={testChannelId} onChange={(e) => setTestChannelId(e.target.value)} disabled={!anyConnected}>
                  <option value="">Use the board's {effectiveTestPlatform === 'gchat' ? 'space' : 'channel'}</option>
                  {testChannels.map((c) => (
                    <option key={c.id} value={c.id}>{effectiveTestPlatform === 'gchat' ? '' : c.isPrivate ? '🔒 ' : '#'}{c.name}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => testAlertMutation.mutate()}
                disabled={!anyConnected || !testTicketId || testAlertMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {testAlertMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
                Send test
              </button>
              {testAlertMutation.isSuccess && (
                <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400"><CheckCircle className="h-4 w-4" /> Posted.</span>
              )}
              {testAlertMutation.isError && (
                <span className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400"><AlertCircle className="h-4 w-4" /> {testAlertMutation.error?.response?.data?.error || 'Failed'}</span>
              )}
            </div>
            {!anyConnected && <p className="mt-2 text-xs text-neutral-500">Connect a chat platform first.</p>}
            {overview.activity?.lastTestAt && (
              <p className="mt-2 inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                <CheckCircle className="h-3.5 w-3.5" /> Last test alert sent {new Date(overview.activity.lastTestAt).toLocaleString()}
              </p>
            )}
            <Why title="Testing the buttons end to end">
              <p>Test cards are read-only. To verify that Take it, status changes, and replies round-trip to ConnectWise, enable alerts for a board and create or update a ticket for a test company of your own. The live card that appears has working buttons.</p>
            </Why>
          </Card>

          {/* Status */}
          <Card id="status" icon={Activity} title="Status" description="Callback health, last event received, background queue, and recent errors.">
            <TicketAlertsStatusPanel overview={overview} onReregister={() => reregisterMutation.mutate()} reregistering={reregisterMutation.isPending} />
            {reregisterMutation.isError && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{reregisterMutation.error?.response?.data?.error || 'Re-register failed'}</p>}
          </Card>

          {/* Catch up */}
          <Card id="catchup" icon={Inbox} title="Catch up" description="Card open tickets that have no card — the ones that came back or failed to post before.">
            <TicketAlertsCatchUp enabled={config.enabled === true} />
          </Card>
        </div>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center gap-3">
        <Bell className="h-6 w-6 text-neutral-400" />
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Ticket Alerts</h1>
      </div>
      <p className="text-neutral-500 dark:text-neutral-400">
        Real-time ConnectWise ticket alerts in Slack, Google Chat or Microsoft Teams, with status changes, assignments, and replies from the card.{' '}
        <Link to="/admin/integrations" className="text-primary-600 hover:underline dark:text-primary-400">All integrations</Link>
      </p>
    </div>
  );
}
